import React, {
  ForwardedRef,
  ReactNode,
  useImperativeHandle,
  useRef,
} from "react";
import styled from "styled-components/macro";
import { Spinner } from "@src/components/elements";
import { UploadDocumentType } from "@src/components/modules/Upload/UploadDocumentType";
import { useDocumentDrop } from "@src/components/modules/Upload/useDocumentDrop";

const StyledUploadForm = styled.form<{ hovered: boolean; error?: boolean }>`
  transition: box-shadow 0.25s ease;
  padding: 1rem 0.5rem 0.5rem;
  background: var(--background-gray);
  box-shadow: ${(props) =>
    props.hovered
      ? "0px 0px 10px rgba(var(--focus-green-rgb), 0.75)"
      : "var(--drop-shadow)"};
  border: 1px solid
    ${(props) =>
      props.error
        ? "var(--error-color)"
        : props.hovered
        ? "var(--border-gray)"
        : "var(--border-gray-light)"};
  border-radius: var(--rounded-corner);
  min-width: 300px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  svg {
    fill: var(--border-gray);
    width: 40px;
    height: 40px;
  }

  p {
    margin: 0;
    color: var(--secondary-black);
  }

  label {
    font-family: var(--inter-font);
    font-size: 0.8rem;
    font-weight: var(--font-weight-bold);
    padding: 0.75rem 1rem;
    border-radius: var(--rounded-corner);
    border: 1px solid var(--border-gray);
    background: var(--page-background);
    color: var(--primary-black);
    transition: var(--transition);
    box-shadow: var(--drop-shadow-light);
    cursor: pointer;
    &:hover {
      background: var(--background-gray);
      box-shadow: var(--drop-shadow);
    }
  }
`;

interface Props {
  allowedTypes: UploadDocumentType[];
  className?: string;
  loading?: boolean;
  onDocsAdded?: (docs: File[]) => void;
  children?: ReactNode;
  id?: string;
  error?: string;
}

export type UploadFormReset = { reset: () => void };

export const Upload = React.forwardRef(
  (
    {
      className,
      children,
      loading,
      allowedTypes,
      onDocsAdded,
      id = "uploadForm",
      error,
    }: Props,
    ref: ForwardedRef<UploadFormReset>,
  ) => {
    const { bond, state, selectFile } = useDocumentDrop({
      onDocsAdded,
      allowedTypes,
    });
    const formEl = useRef<HTMLFormElement>(null);

    useImperativeHandle(ref, () => ({
      reset() {
        return formEl.current?.reset();
      },
    }));

    return (
      <>
        <StyledUploadForm
          className={className}
          {...bond}
          hovered={state.over}
          ref={formEl}
          error={Boolean(error)}
        >
          <svg focusable="false" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"></path>
          </svg>
          <p>Drag and drop</p>
          <p>- or -</p>
          <label htmlFor={id}>Choose a file</label>
          <input
            id={id}
            type="file"
            style={{ display: "none" }}
            multiple={false}
            onChange={selectFile}
          />
          {loading && <Spinner />}
        </StyledUploadForm>
        {children}
      </>
    );
  },
);
